import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { ScanLine, Search, X, Settings, GitCompare, TrendingUp, Award, Users, ChefHat, Gift, Package, AlertTriangle, ArrowRight, Sparkles, Beaker, Mail, Globe, Instagram, Facebook, Twitter, MessageCircle, Youtube, Linkedin } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Link } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { safeArray } from '@/components/utils/safeArray';

import HealthProfile from '@/components/onboarding/HealthProfile';
import ImageUploader from '@/components/scanner/ImageUploader';
import ProductCard from '@/components/products/ProductCard';
import ProductDetail from '@/components/products/ProductDetail';
import CompareProducts from '@/components/products/CompareProducts';
import EmptyState from '@/components/products/EmptyState';
import { awardPoints, POINT_VALUES } from '@/components/loyalty/PointsHelper';
import PointsDisplay from '@/components/loyalty/PointsDisplay';
import LegalDocumentViewer from '@/components/legal/LegalDocumentViewer';
import { FileText, Shield } from 'lucide-react';

export default function Home() {
    const [user, setUser] = useState(null);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showCompare, setShowCompare] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [userPoints, setUserPoints] = useState(null);
    const [selectedLegalDoc, setSelectedLegalDoc] = useState(null);
    const queryClient = useQueryClient();

    // Load user
    useEffect(() => {
        const loadUser = async () => {
            try {
                const currentUser = await base44.auth.me();
                setUser(currentUser);
                
                // No bloquear Home, onboarding se gestiona desde Perfil

                // Load user points
                const points = await base44.entities.UserPoints.filter({ user_id: currentUser.id });
                setUserPoints(points[0] || null);
            } catch (error) {
                console.error('Error loading user:', error);
            }
        };
        loadUser();
    }, []);

    const { data: products = [], isLoading } = useQuery({
        queryKey: ['products', user?.id],
        queryFn: async () => {
            const data = await base44.entities.Product.filter({ created_by: user?.email }, '-created_date');
            return safeArray(data);
        },
        enabled: !!user?.id,
    });

    const { data: recipes = [] } = useQuery({
        queryKey: ['recipes'],
        queryFn: async () => {
            const data = await base44.entities.Recipe.list('-created_date', 5);
            return safeArray(data);
        },
    });

    const { data: groups = [] } = useQuery({
        queryKey: ['groups'],
        queryFn: async () => {
            const data = await base44.entities.Group.list('-created_date', 5);
            return safeArray(data);
        },
    });

    const { data: rewards = [] } = useQuery({
        queryKey: ['rewards'],
        queryFn: async () => {
            const data = await base44.entities.Reward.list();
            return safeArray(data);
        },
    });

    const { data: recentAdditives = [] } = useQuery({
        queryKey: ['recent-additives', user?.id],
        queryFn: async () => {
            const userProducts = safeArray(await base44.entities.Product.filter({ created_by: user?.email }, '-created_date', 10));
            const productIds = userProducts.map(p => p.id);
            const allAdditives = safeArray(await base44.entities.Additive.list('-created_date'));
            return allAdditives.filter(additive => 
                safeArray(additive.product_ids).some(pid => productIds.includes(pid))
            ).slice(0, 5);
        },
        enabled: !!user?.email,
    });

    const { data: recentVitamins = [] } = useQuery({
        queryKey: ['recent-vitamins', user?.id],
        queryFn: async () => {
            const userProducts = safeArray(await base44.entities.Product.filter({ created_by: user?.email }, '-created_date', 10));
            const productIds = userProducts.map(p => p.id);
            const allVitamins = safeArray(await base44.entities.Vitamin.list('-created_date'));
            return allVitamins.filter(vitamin => 
                safeArray(vitamin.product_ids).some(pid => productIds.includes(pid))
            ).slice(0, 5);
        },
        enabled: !!user?.email,
    });

    const { data: legalDocs = [] } = useQuery({
        queryKey: ['legal-documents'],
        queryFn: async () => {
            const data = await base44.entities.LegalDocument.list();
            return safeArray(data);
        },
    });

    const { data: banners = [] } = useQuery({
        queryKey: ['banners'],
        queryFn: async () => {
            const data = await base44.entities.Banner.filter({ active: true, position: 'home_top' });
            return safeArray(data);
        },
    });

    const { data: appSettings } = useQuery({
        queryKey: ['app-settings'],
        queryFn: async () => {
            const settings = safeArray(await base44.entities.AppSettings.filter({ key: 'main' }));
            return settings[0];
        },
    });

    const createProductMutation = useMutation({
        mutationFn: (data) => base44.entities.Product.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['products'] });
            toast.success('¡Producto analizado!');
        },
    });

    const deleteProductMutation = useMutation({
        mutationFn: (id) => base44.entities.Product.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['products'] });
            setSelectedProduct(null);
            toast.success('Producto eliminado');
        },
    });

    const updateProductMutation = useMutation({
        mutationFn: ({ id, data }) => base44.entities.Product.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['products'] });
            toast.success('Producto actualizado');
        },
    });

    const handleProfileComplete = async (profileData) => {
        try {
            await base44.auth.updateMe({
                ...profileData,
                onboarding_completed: true
            });
            setUser({ ...user, ...profileData, onboarding_completed: true });
            setShowOnboarding(false);
            toast.success('¡Perfil configurado!');
        } catch (error) {
            console.error('Error saving profile:', error);
            toast.error('Error al guardar el perfil');
        }
    };

    const generateAnalysisPrompt = (userProfile, ocrText) => {
        const conditions = [
            ...safeArray(userProfile?.health_conditions).filter(c => c !== 'ninguna'),
            ...safeArray(userProfile?.custom_health_conditions)
        ].join(', ') || 'ninguna';
        const goals = safeArray(userProfile?.health_goals).join(', ') || 'comer saludable';
        const preferences = safeArray(userProfile?.dietary_preferences).filter(p => p !== 'ninguna').join(', ') || 'ninguna';

        return `Actuá como nutricionista clínico especializado en educación alimentaria.

${ocrText ? `TEXTO EXTRAÍDO DE LA ETIQUETA (usa esto como referencia principal):
${ocrText}

` : ''}Actuá como nutricionista clínico especializado en educación alimentaria.

PERFIL DEL USUARIO:
- Condiciones de salud: ${conditions}
- Objetivos: ${goals}
- Preferencias dietarias: ${preferences}

TAREA:
Analiza esta etiqueta de producto alimentario y proporciona una evaluación personalizada según el perfil del usuario.

EXTRAE Y ANALIZA:
1. Datos básicos: nombre, marca, categoría (IMPORTANTE: debes categorizar como "comestibles" para alimentos sólidos o "bebidas" para líquidos)
2. Información nutricional completa (calorías, azúcar, sodio, grasas, proteínas, fibra, porción)
3. Lista de ingredientes
4. Aditivos (con código E si aplica) y su nivel de riesgo
5. Vitaminas y minerales presentes (con códigos como B12, D3, C, etc.) y su nivel de beneficio
6. Evaluación personalizada según condiciones del usuario

CRITERIOS DE EVALUACIÓN:
- Para diabetes: evaluar azúcares, carbohidratos, índice glucémico
- Para hipertensión: enfocarse en sodio
- Para colesterol: evaluar grasas saturadas y trans
- Para sobrepeso: analizar calorías, grasas, azúcares
- Para celiaquía: verificar gluten

EVALUACIÓN DE ADITIVOS:
- Analiza TODOS los aditivos detectados contra las condiciones de salud del usuario
- Si un aditivo puede agravar alguna condición del usuario, inclúyelo en las advertencias (warnings)
- Por ejemplo: nitritos/nitratos para personas con hipertensión, edulcorantes artificiales para diabéticos, glutamato monosódico para sensibilidad, etc.
- Sé específico sobre QUÉ aditivo y POR QUÉ es problemático para ESA condición

RESPONDE CON:
- Código de barras si está visible
- URL del sitio web del fabricante si está visible
- Puntaje EscanEat (0-100): 80-100=excelente, 60-79=bueno, 40-59=regular, 0-39=malo
- Semáforo (green/yellow/red) personalizado según el perfil
- Recomendación en lenguaje simple y claro (max 2 líneas)
- 2-4 aspectos positivos concretos
- 2-4 aspectos negativos concretos
- Alertas específicas según condiciones del usuario (si aplica)
- 2-3 alternativas mejores disponibles en supermercados
- Aditivos con explicación simple
- Vitaminas y minerales con sus beneficios

TONO:
- Lenguaje claro y simple, sin tecnicismos
- Educativo, nunca alarmista
- Empático y práctico
- Sin emitir diagnósticos médicos`;
    };

    /**
     * Converts a raw base64 string to a Blob — avoids File constructor issues on Android.
     */
    const base64ToBlob = (base64, mimeType = 'image/jpeg') => {
        const raw = base64.includes(',') ? base64.split(',')[1] : base64;
        const bytes = Uint8Array.from(atob(raw), c => c.charCodeAt(0));
        return new Blob([bytes], { type: mimeType });
    };

    const handleImageCapture = async (base64Images, clearPreviewCallback, searchText = null) => {
        if (!user) {
            toast.error('Debes iniciar sesión para analizar productos');
            return;
        }

        setIsProcessing(true);
        
        try {
            let fileUrls = [];
            let ocrText = '';

            if (searchText) {
                // TEXT SEARCH MODE — no image upload needed
                console.log('[Home] TEXT SEARCH mode:', searchText);
                ocrText = searchText;
            } else {
                // IMAGE MODE
                const base64Array = Array.isArray(base64Images) ? base64Images : [base64Images];
                console.log('[Home] IMAGE mode:', base64Array.length, 'image(s)');

                const uploadPromises = base64Array.map(async (b64, idx) => {
                    const blob = base64ToBlob(b64, 'image/jpeg');
                    const file = new File([blob], `photo_${idx}.jpg`, { type: 'image/jpeg' });
                    return base44.integrations.Core.UploadFile({ file });
                });

                const uploadResults = await Promise.all(uploadPromises);
                fileUrls = uploadResults.map(r => r.file_url);
                console.log('[Home] ✓ Imágenes subidas:', fileUrls);

                // OCR
                const ocrResult = await base44.integrations.Core.InvokeLLM({
                    prompt: "Extrae TODO el texto visible en esta etiqueta de producto. Incluye: nombre, marca, ingredientes, tabla nutricional, código de barras. Devuelve el texto tal como aparece.",
                    file_urls: fileUrls,
                });
                ocrText = typeof ocrResult === 'string' ? ocrResult : JSON.stringify(ocrResult);
                console.log('[Home] ✓ OCR completado');
            }

            const analysisPrompt = generateAnalysisPrompt(user, ocrText);

            console.log('[Home] ▶ Análisis LLM — enviando prompt + imágenes + internet');
            const analysis = await base44.integrations.Core.InvokeLLM({
                prompt: analysisPrompt,
                file_urls: fileUrls,
                add_context_from_internet: true,
                response_json_schema: {
                    type: "object",
                    properties: {
                        name: { type: "string" },
                        brand: { type: "string" },
                        manufacturer_name: { type: "string" },
                        category: { type: "string", enum: ["comestibles", "bebidas"] },
                        barcode: { type: "string" },
                        manufacturer_url: { type: "string" },
                        nutrition_data: {
                            type: "object",
                            properties: {
                                calories: { type: "number" },
                                sugar: { type: "number" },
                                sodium: { type: "number" },
                                fats: { type: "number" },
                                saturated_fats: { type: "number" },
                                protein: { type: "number" },
                                fiber: { type: "number" },
                                serving_size: { type: "string" }
                            }
                        },
                        ingredients: { type: "string" },
                        additives: {
                            type: "array",
                            items: {
                                type: "object",
                                properties: {
                                    name: { type: "string" },
                                    code: { type: "string" },
                                    description: { type: "string" },
                                    risk_level: { type: "string", enum: ["bajo", "moderado", "alto"] }
                                }
                            }
                        },
                        vitamins: {
                            type: "array",
                            items: {
                                type: "object",
                                properties: {
                                    name: { type: "string" },
                                    code: { type: "string" },
                                    description: { type: "string" },
                                    benefit_level: { type: "string", enum: ["bajo", "moderado", "alto"] }
                                }
                            }
                        },
                        scaneat_score: { type: "number", minimum: 0, maximum: 100 },
                        traffic_light: { type: "string", enum: ["green", "yellow", "red"] },
                        recommendation: { type: "string" },
                        pros: { type: "array", items: { type: "string" } },
                        cons: { type: "array", items: { type: "string" } },
                        warnings: { type: "array", items: { type: "string" } },
                        alternatives: { type: "array", items: { type: "string" } }
                    }
                }
            });

            console.log('[Home] ✓ Análisis LLM completado — producto:', analysis?.name, '| score:', analysis?.scaneat_score);
            console.log('[Home] ▶ Guardando producto en BD...');
            const createdProduct = await createProductMutation.mutateAsync({
                ...analysis,
                image_url: fileUrls[0], // Usar la primera imagen como principal
                ocr_text: ocrText
            });

            // Vincular producto con fabricante automáticamente
            if (analysis.brand || analysis.manufacturer_name) {
                try {
                    const manufacturerQuery = analysis.brand || analysis.manufacturer_name;
                    const manufacturers = await base44.entities.Manufacturer.list();
                    const matchedManufacturer = manufacturers.find(m => 
                        m.brand?.toLowerCase().includes(manufacturerQuery.toLowerCase()) ||
                        m.name?.toLowerCase().includes(manufacturerQuery.toLowerCase())
                    );

                    if (matchedManufacturer) {
                        await base44.entities.Manufacturer.update(matchedManufacturer.id, {
                            product_count: (matchedManufacturer.product_count || 0) + 1
                        });
                    } else if (analysis.manufacturer_name) {
                        // Crear fabricante automáticamente si no existe
                        await base44.entities.Manufacturer.create({
                            name: analysis.manufacturer_name,
                            brand: analysis.brand || analysis.manufacturer_name,
                            website: analysis.manufacturer_url,
                            product_count: 1
                        });
                    }
                } catch (error) {
                    console.error('Error linking manufacturer:', error);
                }
            }

            // Award points for scanning
            if (user?.id) {
                await awardPoints(user.id, 'scan', POINT_VALUES.scan, `Escaneó: ${createdProduct.name}`, createdProduct.id);
                const updatedPoints = await base44.entities.UserPoints.filter({ user_id: user.id });
                setUserPoints(updatedPoints[0] || null);

                // Track activity
                await base44.entities.ProductActivity.create({
                    product_id: createdProduct.id,
                    activity_type: 'scan',
                    user_id: user.id
                });
            }

            // Save additives to database
            if (safeArray(analysis.additives).length > 0) {
                for (const additive of safeArray(analysis.additives)) {
                    try {
                        const existing = await base44.entities.Additive.filter({ code: additive.code });
                        if (existing.length > 0) {
                            const existingAdditive = existing[0];
                            const productIds = existingAdditive.product_ids || [];
                            if (!productIds.includes(createdProduct.id)) {
                                await base44.entities.Additive.update(existingAdditive.id, {
                                    product_ids: [...productIds, createdProduct.id]
                                });
                            }
                        } else {
                            await base44.entities.Additive.create({
                                code: additive.code,
                                name: additive.name,
                                description: additive.description,
                                risk_level: additive.risk_level,
                                product_ids: [createdProduct.id]
                            });
                        }
                    } catch (err) {
                        console.error('Error saving additive:', err);
                    }
                }
            }

            // Save vitamins to database
            if (safeArray(analysis.vitamins).length > 0) {
                for (const vitamin of safeArray(analysis.vitamins)) {
                    try {
                        const existing = await base44.entities.Vitamin.filter({ code: vitamin.code });
                        if (existing.length > 0) {
                            const existingVitamin = existing[0];
                            const productIds = existingVitamin.product_ids || [];
                            if (!productIds.includes(createdProduct.id)) {
                                await base44.entities.Vitamin.update(existingVitamin.id, {
                                    product_ids: [...productIds, createdProduct.id]
                                });
                            }
                        } else {
                            await base44.entities.Vitamin.create({
                                code: vitamin.code,
                                name: vitamin.name,
                                description: vitamin.description,
                                benefit_level: vitamin.benefit_level,
                                product_ids: [createdProduct.id]
                            });
                        }
                    } catch (err) {
                        console.error('Error saving vitamin:', err);
                    }
                }
            }
            
            // Clear preview after successful analysis
            if (clearPreviewCallback) {
                clearPreviewCallback();
            }
            
        } catch (error) {
            console.error('[Home] ✗ ERROR en análisis:', error?.message || error);
            console.error('[Home] Stack:', error?.stack);
            toast.error(`Error: ${error?.message || 'No se pudo analizar el producto'}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const filteredProducts = products.filter(product => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            product.name?.toLowerCase().includes(query) ||
            product.brand?.toLowerCase().includes(query) ||
            product.category?.toLowerCase().includes(query)
        );
    });

    const calculateHealthScore = () => {
        const productsArray = safeArray(products);
        if (productsArray.length === 0) return null;
        const scores = productsArray.filter(p => p.scaneat_score !== undefined).map(p => p.scaneat_score);
        if (scores.length === 0) return null;
        return Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length);
    };

    const healthScore = calculateHealthScore();
    const recentProducts = safeArray(products).slice(0, 3);

    return (
        <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-gray-50 overflow-x-hidden w-full" style={{maxWidth: '100vw'}}>
            {/* Header */}
            <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-40 safe-header">
                <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2 sm:py-3">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                            <div className="w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                                <ScanLine className="w-4 h-4 text-white" />
                            </div>
                            <h1 className="font-bold text-gray-900 text-xs sm:text-sm line-clamp-2 leading-tight">
                                Hola, {user?.full_name?.split(' ')[0] || 'Usuario'} 👋
                            </h1>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                            {userPoints && <PointsDisplay userPoints={userPoints} compact />}
                            {/* Social Links - hidden on very small screens, shown on sm+ */}
                            <div className="hidden sm:flex items-center gap-1">
                                {appSettings?.contact_email && (
                                    <a href={`mailto:${appSettings.contact_email}`} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors" title="Email">
                                        <Mail className="w-4 h-4 text-gray-600" />
                                    </a>
                                )}
                                {appSettings?.website && (
                                    <a href={appSettings.website} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors" title="Sitio Web">
                                        <Globe className="w-4 h-4 text-gray-600" />
                                    </a>
                                )}
                                {appSettings?.instagram && (
                                    <a href={`https://instagram.com/${appSettings.instagram}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors" title="Instagram">
                                        <Instagram className="w-4 h-4 text-gray-600" />
                                    </a>
                                )}
                                {appSettings?.facebook && (
                                    <a href={appSettings.facebook} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors" title="Facebook">
                                        <Facebook className="w-4 h-4 text-gray-600" />
                                    </a>
                                )}
                                {appSettings?.youtube && (
                                    <a href={appSettings.youtube.startsWith('http') ? appSettings.youtube : `https://youtube.com/${appSettings.youtube}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors" title="YouTube">
                                        <Youtube className="w-4 h-4 text-gray-600" />
                                    </a>
                                )}
                                {appSettings?.linkedin && (
                                    <a href={appSettings.linkedin} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors" title="LinkedIn">
                                        <Linkedin className="w-4 h-4 text-gray-600" />
                                    </a>
                                )}
                                {appSettings?.whatsapp && (
                                    <a href={`https://wa.me/${appSettings.whatsapp}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors" title="WhatsApp">
                                        <MessageCircle className="w-4 h-4 text-gray-600" />
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <main className="w-full max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4" style={{boxSizing: 'border-box'}}>
                <div className="grid lg:grid-cols-3 gap-3 sm:gap-4 w-full">
                    {/* Left Column - Scanner + Stats */}
                    <div className="lg:col-span-2 space-y-3 sm:space-y-4 min-w-0 w-full overflow-hidden">
                        {/* Scanner */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl sm:rounded-3xl p-4 sm:p-6 text-white shadow-lg w-full overflow-hidden"
                        >
                            <div className="flex items-center gap-2 mb-2 sm:mb-3">
                                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
                                <span className="text-sm font-medium">Analizar producto</span>
                            </div>
                            <p className="text-xs sm:text-sm opacity-90 mb-3 sm:mb-4">Buscá por nombre o subí una foto de la etiqueta</p>
                            <ImageUploader 
                                onImageCapture={handleImageCapture}
                                isProcessing={isProcessing}
                            />
                        </motion.div>

                        {/* Stats Grid */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 w-full"
                        >
                            <Card className="p-3 sm:p-4">
                                <Package className="w-4 h-4 text-blue-600 mb-1.5" />
                                <p className="text-xl sm:text-2xl font-bold text-gray-900">{products.length}</p>
                                <p className="text-xs text-gray-500">Productos</p>
                            </Card>

                            {healthScore && (
                                <Card className="p-3 sm:p-4">
                                    <Award className="w-4 h-4 text-emerald-600 mb-1.5" />
                                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{healthScore}</p>
                                    <p className="text-xs text-gray-500">Índice salud</p>
                                </Card>
                            )}

                            <Card className="p-3 sm:p-4">
                                <Gift className="w-4 h-4 text-amber-600 mb-1.5" />
                                <p className="text-xl sm:text-2xl font-bold text-gray-900">{userPoints?.available_points || 0}</p>
                                <p className="text-xs text-gray-500">Puntos</p>
                            </Card>

                            <Card className="p-3 sm:p-4">
                                <ChefHat className="w-4 h-4 text-purple-600 mb-1.5" />
                                <p className="text-xl sm:text-2xl font-bold text-gray-900">{recipes.length}</p>
                                <p className="text-xs text-gray-500">Recetas</p>
                            </Card>
                        </motion.div>

                        {/* Recent Products */}
                        {safeArray(recentProducts).length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="font-semibold text-gray-900 line-clamp-1">Últimos productos</h2>
                                    <Link to={createPageUrl('Profile')}>
                                        <Button variant="ghost" size="sm" className="text-xs">
                                            Ver todos <ArrowRight className="w-3 h-3 ml-1" />
                                        </Button>
                                    </Link>
                                </div>
                                <div className="grid gap-3">
                                    {safeArray(recentProducts).map((product) => (
                                        <ProductCard
                                            key={product.id}
                                            product={product}
                                            onClick={() => setSelectedProduct(product)}
                                        />
                                    ))}
                                </div>
                            </motion.div>
                        )}

                        {/* Recent Additives */}
                        {safeArray(recentAdditives).length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <Beaker className="w-5 h-5 text-purple-600" />
                                        <h2 className="font-semibold text-gray-900 line-clamp-2 leading-tight">Últimos aditivos detectados</h2>
                                    </div>
                                    <Link to={createPageUrl('Profile')}>
                                        <Button variant="ghost" size="sm" className="text-xs">
                                            Ver todos <ArrowRight className="w-3 h-3 ml-1" />
                                        </Button>
                                    </Link>
                                </div>
                                <div className="space-y-3">
                                    {safeArray(recentAdditives).map((additive) => {
                                        const colors = {
                                            bajo: { bg: 'bg-emerald-50', text: 'text-emerald-700', badge: 'bg-emerald-100' },
                                            moderado: { bg: 'bg-amber-50', text: 'text-amber-700', badge: 'bg-amber-100' },
                                            alto: { bg: 'bg-red-50', text: 'text-red-700', badge: 'bg-red-100' }
                                        }[additive.risk_level] || { bg: 'bg-gray-50', text: 'text-gray-700', badge: 'bg-gray-100' };

                                        return (
                                            <Card key={additive.id} className={`p-3 ${colors.bg} border-none`}>
                                                <div className="flex items-start gap-3">
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${colors.badge} ${colors.text}`}>
                                                        {additive.code}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium text-gray-900 text-sm">{additive.name}</p>
                                                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">{additive.description}</p>
                                                        <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full font-medium ${colors.badge} ${colors.text}`}>
                                                            Riesgo {additive.risk_level}
                                                        </span>
                                                    </div>
                                                </div>
                                            </Card>
                                        );
                                    })}
                                </div>
                            </motion.div>
                        )}

                        {/* Recent Vitamins */}
                        {safeArray(recentVitamins).length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.35 }}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <Sparkles className="w-5 h-5 text-emerald-600" />
                                        <h2 className="font-semibold text-gray-900 line-clamp-2 leading-tight">Últimas vitaminas detectadas</h2>
                                    </div>
                                    <Link to={createPageUrl('Profile')}>
                                        <Button variant="ghost" size="sm" className="text-xs">
                                            Ver todos <ArrowRight className="w-3 h-3 ml-1" />
                                        </Button>
                                    </Link>
                                </div>
                                <div className="space-y-3">
                                    {safeArray(recentVitamins).map((vitamin) => {
                                        const colors = {
                                            alto: { bg: 'bg-emerald-50', text: 'text-emerald-700', badge: 'bg-emerald-100' },
                                            moderado: { bg: 'bg-blue-50', text: 'text-blue-700', badge: 'bg-blue-100' },
                                            bajo: { bg: 'bg-gray-50', text: 'text-gray-700', badge: 'bg-gray-100' }
                                        }[vitamin.benefit_level] || { bg: 'bg-gray-50', text: 'text-gray-700', badge: 'bg-gray-100' };

                                        return (
                                            <Card key={vitamin.id} className={`p-3 ${colors.bg} border-none`}>
                                                <div className="flex items-start gap-3">
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${colors.badge} ${colors.text}`}>
                                                        {vitamin.code}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium text-gray-900 text-sm">{vitamin.name}</p>
                                                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">{vitamin.description}</p>
                                                        <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full font-medium ${colors.badge} ${colors.text}`}>
                                                            Beneficio {vitamin.benefit_level}
                                                        </span>
                                                    </div>
                                                </div>
                                            </Card>
                                        );
                                    })}
                                </div>
                            </motion.div>
                        )}
                    </div>

                    {/* Right Column */}
                    <div className="space-y-3 sm:space-y-4 min-w-0 w-full overflow-hidden">
                        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
                            <h3 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Acciones rápidas</h3>
                            <div className="grid grid-cols-2 lg:grid-cols-1 gap-2 sm:gap-3">
                                <Link to={createPageUrl('Rewards')}>
                                    <Card className="p-3 sm:p-4 hover:shadow-md transition-shadow cursor-pointer bg-gradient-to-br from-amber-50 to-orange-50 h-full">
                                        <div className="flex items-center gap-2 sm:gap-3">
                                            <Gift className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-gray-900 text-sm">Recompensas</p>
                                                <p className="text-xs text-gray-600 hidden sm:block">{rewards.length} disponibles</p>
                                            </div>
                                            <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                                        </div>
                                    </Card>
                                </Link>

                                <Link to={createPageUrl('Recipes')}>
                                    <Card className="p-3 sm:p-4 hover:shadow-md transition-shadow cursor-pointer bg-gradient-to-br from-purple-50 to-pink-50 h-full">
                                        <div className="flex items-center gap-2 sm:gap-3">
                                            <ChefHat className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-gray-900 text-sm">Recetas</p>
                                                <p className="text-xs text-gray-600 hidden sm:block">Personalizadas</p>
                                            </div>
                                            <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                                        </div>
                                    </Card>
                                </Link>

                                <Link to={createPageUrl('Community')}>
                                    <Card className="p-3 sm:p-4 hover:shadow-md transition-shadow cursor-pointer bg-gradient-to-br from-blue-50 to-indigo-50 h-full">
                                        <div className="flex items-center gap-2 sm:gap-3">
                                            <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-gray-900 text-sm">Comunidad</p>
                                                <p className="text-xs text-gray-600 hidden sm:block">Comparte y aprende</p>
                                            </div>
                                            <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                                        </div>
                                    </Card>
                                </Link>

                                {products.length >= 2 && (
                                    <Card 
                                        className="p-3 sm:p-4 hover:shadow-md transition-shadow cursor-pointer bg-gradient-to-br from-green-50 to-emerald-50"
                                        onClick={() => setShowCompare(true)}
                                    >
                                        <div className="flex items-center gap-2 sm:gap-3">
                                            <GitCompare className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-gray-900 text-sm">Comparar</p>
                                                <p className="text-xs text-gray-600 hidden sm:block">Productos</p>
                                            </div>
                                            <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                                        </div>
                                    </Card>
                                )}
                            </div>
                        </motion.div>

                        {safeArray(groups).length > 0 && (
                            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-semibold text-gray-900">Grupos populares</h3>
                                    <Link to={createPageUrl('Groups')}>
                                        <Button variant="ghost" size="sm" className="text-xs">Ver todos</Button>
                                    </Link>
                                </div>
                                <div className="space-y-2">
                                    {safeArray(groups).slice(0, 3).map((group) => (
                                        <Link key={group.id} to={createPageUrl('Community')}>
                                            <Card className="p-3 hover:shadow-md transition-shadow">
                                                <p className="font-medium text-gray-900 text-sm">{group.name}</p>
                                                <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                                                    <Users className="w-3 h-3" />
                                                    <span>{group.member_count || 0} miembros</span>
                                                </div>
                                            </Card>
                                        </Link>
                                    ))}
                                </div>
                            </motion.div>
                        )}

                        {safeArray(user?.health_conditions).length > 0 && (
                            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
                                <Card className="p-4 bg-gradient-to-br from-yellow-50 to-amber-50">
                                    <div className="flex items-start gap-3">
                                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="font-medium text-gray-900 text-sm mb-1">Consejo</p>
                                            <p className="text-xs text-gray-600">Revisa las etiquetas según tus condiciones de salud</p>
                                        </div>
                                    </div>
                                </Card>
                            </motion.div>
                        )}

                        {/* Banners Publicitarios */}
                        {safeArray(banners).length > 0 && (
                           <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}>
                               <h3 className="font-semibold text-gray-900 mb-3">Publicidad</h3>
                               <div className="space-y-3">
                                   {safeArray(banners).map((banner) => (
                                       <a
                                           key={banner.id}
                                           href={banner.link || '#'}
                                           target={banner.link ? "_blank" : undefined}
                                           rel="noopener noreferrer"
                                           className="block rounded-xl overflow-hidden hover:opacity-90 transition-opacity shadow-sm"
                                       >
                                           <img
                                               src={banner.image_url}
                                               alt={banner.name}
                                               className="w-full h-32 object-cover"
                                           />
                                       </a>
                                   ))}
                               </div>
                           </motion.div>
                        )}

                        {/* Legal Documents */}
                        {safeArray(legalDocs).length > 0 && (
                            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}>
                                <h3 className="font-semibold text-gray-900 mb-3">Legal</h3>
                                <div className="space-y-2">
                                    {safeArray(legalDocs).map((doc) => (
                                        <Card 
                                            key={doc.id}
                                            className="p-3 hover:shadow-md transition-shadow cursor-pointer"
                                            onClick={() => setSelectedLegalDoc(doc)}
                                        >
                                            <div className="flex items-center gap-2">
                                                {doc.slug === 'terms' ? (
                                                    <FileText className="w-4 h-4 text-gray-600" />
                                                ) : (
                                                    <Shield className="w-4 h-4 text-gray-600" />
                                                )}
                                                <p className="text-sm font-medium text-gray-900 flex-1">{doc.title}</p>
                                                <ArrowRight className="w-3 h-3 text-gray-400" />
                                            </div>
                                        </Card>
                                    ))}
                                </div>
                            </motion.div>
                        )}
                    </div>
                </div>
            </main>

            {selectedProduct && (
                <ProductDetail
                    product={selectedProduct}
                    onClose={() => setSelectedProduct(null)}
                    onDelete={deleteProductMutation.mutate}
                    onEdit={(id, data) => updateProductMutation.mutate({ id, data })}
                />
            )}

            {showCompare && (
                <CompareProducts
                    products={safeArray(products).filter(p => p.scaneat_score !== undefined)}
                    onClose={() => setShowCompare(false)}
                    currentUser={user}
                />
            )}

            {selectedLegalDoc && (
                <LegalDocumentViewer
                    document={selectedLegalDoc}
                    onClose={() => setSelectedLegalDoc(null)}
                />
            )}
        </div>
    );
}